<template>
    <div
        ref="stepRef"
        class="setup-system-prompt flex flex-col min-h-0"
        :class="isPromptsExpanded ? 'overflow-y-auto' : 'overflow-hidden'"
    >
        <AiAnalysisPicker
            ref="pickerRef"
            class="flex-1"
            :class="isPromptsExpanded ? 'min-h-[180px]' : 'min-h-0'"
            :style="pinnedPickerStyle"
            :value="selectedPresetId"
            :presets="templates"
            :categories="categories"
            :is-loading="isLoading"
            @select-preset="onSelectPreset"
        />
        <div
            ref="instructionsRef"
            class="setup-system-prompt__instructions shrink-0 pt-3"
        >
            <button
                type="button"
                class="setup-system-prompt__own shrink-0 w-full flex-center gap-1.5 rounded-lg border border-dashed py-2 mb-3 text-sm font-semibold text-primary-actions"
                @click="onWriteMyOwn"
            >
                <RedesignBaseIcon name="IconSparkles" />
                {{ t('widget.aiBuilder.analysis.writeMyOwn') }}
            </button>

            <VcCollapse v-model="expandedPanels">
                <VcCollapseItem
                    :name="PROMPTS_PANEL_NAME"
                    class="prompts-collapse-item"
                    :has-border="false"
                    :header-class="'bg-gray-200 rounded p-2'"
                >
                    <template #title>
                        <div class="flex items-center gap-2 w-full">
                            <span class="text-sm font-medium">
                                {{ t('widget.aiBuilder.systemInstructions') }}
                            </span>
                            <RedesignTag
                                v-if="selectedPresetId"
                                :tag-content="[ { text: String(t('widget.aiBuilder.autoTuned')) } ]"
                                padding-classes="px-1.5 py-0.5"
                            />
                            <span class="ml-auto text-sm font-medium text-primary-actions">
                                {{ t('widget.aiBuilder.reviewOrEdit') }}
                            </span>
                        </div>
                    </template>

                    <AiPromptsEditor
                        :system-prompt="systemPrompt"
                        :user-prompt="userPrompt"
                        @change-system-prompt="onPromptChange('system', $event)"
                        @change-user-prompt="onPromptChange('user', $event)"
                    />
                </VcCollapseItem>
            </VcCollapse>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { VcCollapse, VcCollapseItem } from '@voicenter-team/voicenter-ui-plus'

import RedesignTag from '@/components/RedesignBaseComponents/RedesignTag.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import AiAnalysisPicker from '@/components/RedesignComponents/Widgets/Parameters/AiAnalysisPicker.vue'
import AiPromptsEditor from '@/components/RedesignComponents/Widgets/Parameters/AiPromptsEditor.vue'
import { useAiPromptTemplates } from '@/composables/useAiPromptTemplates'
import { type CategorisedPromptTemplate } from '@/api/widgetParameterApi'
import { WidgetConfig } from '@/types/dashboard'

const PROMPTS_PANEL_NAME = 'prompts'

const { t } = useI18n()

const props = defineProps<{
    value: WidgetConfig
}>()

const emit = defineEmits<{
    (e: 'input', value: WidgetConfig): void
    (e: 'update:value', value: WidgetConfig): void
}>()

const {
    templates,
    categories,
    isLoading,
    fetchPromptTemplates,
    findPromptTemplateByPrompts
} = useAiPromptTemplates()

const systemPrompt = computed(() => props.value.WidgetParameterValueJson?.system ?? '')
const userPrompt = computed(() => props.value.WidgetParameterValueJson?.user ?? '')
const hasPrompts = computed(() => !!(systemPrompt.value || userPrompt.value))

const selectedPresetId = computed(() => {
    return findPromptTemplateByPrompts(systemPrompt.value, userPrompt.value)?.PromptTemplateID
})

const expandedPanels = ref<Array<string>>([])

const isPromptsExpanded = computed(() => expandedPanels.value.includes(PROMPTS_PANEL_NAME))

const pickerRef = ref<{ $el: HTMLElement } | null>(null)
const stepRef = ref<HTMLElement | null>(null)
const instructionsRef = ref<HTMLElement | null>(null)
const pinnedPickerHeight = ref<number | null>(null)

const pinnedPickerStyle = computed(() => {
    return pinnedPickerHeight.value
        ? { minHeight: `${pinnedPickerHeight.value}px` }
        : undefined
})

watch(isPromptsExpanded, expanded => {
    if (!expanded) {
        pinnedPickerHeight.value = null
        return
    }

    const height = pickerRef.value?.$el?.clientHeight
    pinnedPickerHeight.value = height || null

    scrollToInstructions()
})

async function scrollToInstructions () {
    setTimeout(() => {
        const instructions = instructionsRef.value

        if (instructions) {
            instructions.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest'
            })
        }
    }, 500)
}

function expandPrompts () {
    if (!expandedPanels.value.includes(PROMPTS_PANEL_NAME)) {
        expandedPanels.value = [ ...expandedPanels.value, PROMPTS_PANEL_NAME ]
    }
}

function emitPrompts (system: string, user: string) {
    const next: WidgetConfig = {
        ...props.value,
        WidgetParameterValueJson: {
            ...(props.value.WidgetParameterValueJson || {}),
            system,
            user
        }
    }

    emit('input', next)
    emit('update:value', next)
}

function onPromptChange (key: 'system' | 'user', text: string) {
    emitPrompts(
        key === 'system' ? text : systemPrompt.value,
        key === 'user' ? text : userPrompt.value
    )
}

function onSelectPreset (preset: CategorisedPromptTemplate) {
    emitPrompts(preset.SystemPrompt, preset.UserPrompt)
}

function onWriteMyOwn () {
    emitPrompts('', '')
    expandPrompts()
}

onMounted(async () => {
    await fetchPromptTemplates()

    if (hasPrompts.value && !selectedPresetId.value) {
        expandPrompts()
    }
})
</script>

<style lang="scss">
.setup-system-prompt {
  &__own {
    border-color: var(--secondary-actions);

    &:hover {
      border-color: var(--active-elements);
    }
  }
}
</style>
